import axios from 'axios';
import type { Assignee } from '../types/assignee';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Types
export enum CalendarEventType {
  Appointment = 1,
  Reminder = 2,
  Committee = 3
}

export interface CalendarEvent {
  id: number;
  title: string;
  description?: string;
  eventType: CalendarEventType;
  eventTypeName: string;
  startDateTime: string;
  endDateTime?: string;

  // For Reminders
  reminderType?: number;
  reminderTypeName?: string;
  reminderPriority?: number;
  reminderPriorityName?: string;
  reminderStatus?: number;

  // For Appointments
  appointmentType?: number;
  appointmentTypeName?: string;
  appointmentStatus?: number;
  appointmentStatusName?: string;
  doctorName?: string;
  meetingType?: number;
  meetingTypeName?: string;

  // Linked entity information
  entityType?: number;
  entityId?: number;
  clientName?: string;
  clientId?: number;

  // Visual properties for calendar display
  color: string;
  isOverdue: boolean;
  isHighPriority: boolean;

  // Metadata
  assignees: Assignee[]; // ריק ⇒ תזכורת לא-משויכת (מאגר המנהלים)
  createdAt: string;
}

// Scope of the calendar (workload) view.
// Server rules: Employee may use 'Mine' or 'Department' (own departments only);
// Manager may use all three. Default is always 'Mine'.
export type CalendarScope = 'Mine' | 'Department' | 'All';

export interface GetCalendarEventsOptions {
  scope?: CalendarScope;
  departmentIds?: number[]; // used when scope === 'Department' (sent as CSV)
  userId?: number;          // optional per-employee filter within the resolved scope
}

// API calls
export const calendarApi = {
  // Get calendar events within a date range.
  // Backward-friendly: when opts is omitted, scope defaults to 'Mine'.
  getCalendarEvents: async (
    startDate: string,
    endDate: string,
    opts?: GetCalendarEventsOptions
  ): Promise<CalendarEvent[]> => {
    const params: Record<string, string | number> = {
      startDate,
      endDate,
      scope: opts?.scope ?? 'Mine'
    };

    if (opts?.departmentIds && opts.departmentIds.length > 0) {
      params.departmentIds = opts.departmentIds.join(',');
    }

    if (opts?.userId != null) {
      params.userId = opts.userId;
    }

    const response = await axios.get(`${API_BASE_URL}/calendar/events`, { params });
    return response.data;
  }
};

export default calendarApi;
